import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ISatisfactionSurveyResponse } from '../../../../extViewmodels/sdk/models';
import { ICreateSocialMediaPostRequest, ILocationState } from '../../../../models';
import {
	EDuplicateType,
	getDuplicateTemplateType,
	shouldShowDuplicateTemplateWarning,
} from '../../../../models/Campaigns';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	getSocialMediaPlainTextContentFromRawRichContent,
	isScheduledOrPendingOrDraftOrNew,
} from '../../../../models/UiUtils';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { ActiveGoogleBusinessReview } from '../../../components/Happiness/DashboardGoogleBusinessProfileCard';
import { useCreateEditSocialMediaContext } from './context';

// Extend ICreateSocialMediaPostRequest to include campaignName
interface ExtendedCreateRequest extends ICreateSocialMediaPostRequest<Api.ITemplate | string> {
	campaignName?: string;
}

// Define a type for testimonial sources
type TestimonialSource =
	| { type: 'google_review'; data: ActiveGoogleBusinessReview }
	| { type: 'survey_response'; data: ISatisfactionSurveyResponse };

// Extend the location state with testimonial properties
interface ExtendedLocationState
	extends ILocationState<
		Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel | Api.ContentCalenderSuggestionViewModel,
		ExtendedCreateRequest
	> {
	isTestimonialMode?: boolean;
	isReviewMode?: boolean;
	review?: ActiveGoogleBusinessReview;
	surveyResponse?: ISatisfactionSurveyResponse;
	surveyResponseId?: string;
	googleReviewId?: string;
	type?: 'google_review' | 'survey_response';
	generatedImage?: string;
	fileAttachment?: any;
	initialContent?: Api.IRawRichTextContentState;
	aiReference?: Api.IAIReference;
}

export function useEditSocialMediaPost({
	impersonationContext,
}: {
	impersonationContext?: ImpersonationContextViewModel;
}) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EditSocialMediaPost');
	const [context] = useCreateEditSocialMediaContext();
	const [isLoaded, setIsLoaded] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [userIsLoaded, setUserIsLoaded] = React.useState(false);
	const routeMatch = useRouteMatch<{ templateId?: string; suggestionId?: string }>();
	const suggestionId = routeMatch?.params?.suggestionId;
	const h = useHistory<ExtendedLocationState>();

	const [postReport, setPostReport] = React.useState<Api.SocialMediaPostReportViewModel>(
		h.location.state?.viewModel instanceof Api.SocialMediaPostReportViewModel ? h.location.state.viewModel : null
	);
	const [postToEdit, setPostToEdit] = React.useState<Api.SocialMediaPostViewModel>(
		h.location.state?.viewModel instanceof Api.SocialMediaPostViewModel ? h.location.state.viewModel : null
	);
	React.useEffect(() => {
		if (h.location.state?.viewModel instanceof Api.SocialMediaPostViewModel) {
			setPostToEdit(h.location.state.viewModel as Api.SocialMediaPostViewModel);
		} else if (h.location.state?.viewModel instanceof Api.SocialMediaPostReportViewModel) {
			setPostReport(h.location.state.viewModel as Api.SocialMediaPostReportViewModel);
		}
	}, [h.location.state?.viewModel]);
	React.useEffect(() => {
		if (h.location.state?.initialContent) {
			context.setContent(
				getSocialMediaPlainTextContentFromRawRichContent(
					h.location.state.initialContent as Api.IRawRichTextContentState
				)
			);
		}
		if (h.location.state?.aiReference) {
			context.setAIReference(h.location.state.aiReference);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [h.location.state?.initialContent, userSession]);
	const source = postReport || postToEdit;

	const locationStateCreateRequest = React.useRef(h.location.state?.model).current;

	const impersonationCtx = impersonationContext || postReport?.impersonationContext;

	const templates = React.useRef<Api.TemplatesViewModel>(
		new Api.TemplatesViewModel(userSession).impersonate(impersonationCtx)
	).current;

	const templateId = React.useRef(
		routeMatch.params?.templateId ||
			(typeof locationStateCreateRequest?.context === 'string'
				? (locationStateCreateRequest?.context as string)
				: locationStateCreateRequest?.context?.id)
	).current;

	const [duplicateType, setDuplicateType] = React.useState<EDuplicateType | null>(null);
	const [showDuplicateWarning, setShowDuplicateWarning] = React.useState(false);

	const [scheduledPost, setScheduledPost] = React.useState<Api.SocialMediaPostViewModel>(null);
	const [socialMediaConnections, setSocialMediaConnections] = React.useState<Api.ISocialMediaConnection[]>([]);
	const initialSelectedUserOptionRef = React.useRef<ISelectOption<Api.UserViewModel<Api.IUser>>>(null);

	// Handle testimonial data from location state - either survey response or Google review
	const testimonialSource = React.useMemo<TestimonialSource | null>(() => {
		if (h.location?.state?.type === 'survey_response' && h.location?.state?.surveyResponse) {
			return {
				type: 'survey_response',
				data: h.location.state.surveyResponse,
			};
		} else if ((h.location?.state?.review || h.location?.state?.isReviewMode) && h.location?.state?.review) {
			return {
				type: 'google_review',
				data: h.location.state.review,
			};
		}
		return null;
	}, [h.location?.state]);

	// Check if we're in testimonial mode (either survey or review)
	const isTestimonialMode = React.useMemo(() => {
		return (
			testimonialSource !== null ||
			h.location?.state?.isTestimonialMode === true ||
			h.location?.state?.isReviewMode === true ||
			h.location?.pathname?.includes('create-from-review')
		);
	}, [h.location?.state, h.location?.pathname, testimonialSource]);

	// Get campaign name from model or generate from testimonial
	const initialCampaignName = React.useMemo(() => {
		// First check if explicitly provided
		if (h.location?.state?.model?.campaignName) {
			return h.location.state.model.campaignName;
		}

		// Otherwise generate based on testimonial source
		if (testimonialSource) {
			if (testimonialSource.type === 'google_review') {
				return testimonialSource.data.author ? `Review from ${testimonialSource.data.author}` : 'Google Review';
			} else if (testimonialSource.type === 'survey_response') {
				const name = testimonialSource.data.contact
					? `${testimonialSource.data.contact.firstName || ''} ${testimonialSource.data.contact.lastName || ''}`.trim()
					: 'Anonymous';

				return `Testimonial from ${name || 'Survey Response'}`;
			}
		}

		return 'New Testimonial';
	}, [h.location?.state?.model, testimonialSource]);

	React.useEffect(() => {
		if (!isLoaded && !isLoading) {
			(async () => {
				setIsLoading(true);
				const account = impersonationCtx?.account || userSession.account;
				const alwaysOverlayLogo = account?.features?.socialMedia?.alwaysOverlayLogo;
				const hideWhiteBackground = account?.features?.socialMedia?.hideWhiteBackground;
				context.setOverlayLogo(alwaysOverlayLogo);
				context.setHideWhiteBackground(hideWhiteBackground);

				// Always ensure the creator is set for testimonial mode
				if (isTestimonialMode) {
					// Set creator to current user if not already set
					if (!context.selectedCreator) {
						context.setSelectedCreator(userSession.user);
					}

					// If we have a postToEdit (from the testimonial image editor), set it as scheduledPost early
					if (postToEdit && !scheduledPost) {
						setScheduledPost(postToEdit);
					}
				}

				// Check if viewModel contains an image attachment we should use
				const viewModel = h.location?.state?.viewModel;
				let viewModelAttachments: Api.IFileAttachment[] = [];

				// Handle different view model types
				if (viewModel instanceof Api.SocialMediaPostViewModel && viewModel.attachments) {
					viewModelAttachments = viewModel.attachments;

					// Ensure that the selected creator is set correctly for the Post Now button to appear
					if (isTestimonialMode && !context.selectedCreator) {
						context.setSelectedCreator(userSession.user);
					}
				}

				if (viewModelAttachments && viewModelAttachments.length > 0) {
					const testimonialImage = viewModelAttachments.find(
						(a: Api.IFileAttachment) => a.fileName?.includes('testimonial-image') || a.mimeType?.startsWith('image/')
					);

					if (testimonialImage) {
						context.setImages([testimonialImage]);
					}
				}
				// Handle testimonial mode initialization (either survey response or google review)
				if (isTestimonialMode && testimonialSource) {
					try {
						let testimonialText = '';

						// Get content based on source type
						if (testimonialSource.type === 'google_review') {
							testimonialText = testimonialSource.data.text || '';
						} else if (testimonialSource.type === 'survey_response') {
							testimonialText = testimonialSource.data.feedback || '';
						}

						// Only initialize from testimonial source if we don't have a loaded post
						if (!postToEdit) {
							// Initialize content with testimonial text if available
							if (testimonialText) {
								context.setContent(testimonialText);
							}

							// Set a campaign name
							context.setCampaignName(initialCampaignName);

							// Set testimonial mode flags
							context.setIsReviewMode(true); // Use review mode flag for both types

							// Mark as loaded
							setIsLoaded(true);
							setIsLoading(false);

							return;
						} else {
							// If we have a postToEdit, we'll let the normal post loading logic handle it
							// but still set the testimonial mode flag
							context.setIsReviewMode(true);
						}
					} catch (error) {
						console.error('Error initializing testimonial mode:', error);
						errorMessages.pushApiError(error);
					}
				}

				const loadFromSocialMediaTemplate = (socialMediaTemplate: Api.ITemplate) => {
					if (socialMediaTemplate?.templateType !== Api.TemplateType.SocialMediaPost) {
						return;
					}

					const dupType = getDuplicateTemplateType(socialMediaTemplate);
					setDuplicateType(dupType);

					const shouldShowWarning = shouldShowDuplicateTemplateWarning(socialMediaTemplate);
					if (shouldShowWarning) {
						setShowDuplicateWarning(shouldShowWarning);
					}

					context.setContent(getSocialMediaPlainTextContentFromRawRichContent(socialMediaTemplate.content));

					context.setImages(socialMediaTemplate.attachments);

					context.setTemplate(socialMediaTemplate);

					context.setHasChanges(false);

					context.setCampaignName(socialMediaTemplate.name);
				};

				const resolveSocialMediaPostTemplate = async (id: string) => {
					try {
						const template = await templates.getByIdExpandedByLastUsedBy(id);

						// check the template type and try to load socal media template from "associatedTemplates" collection if
						// the provided "templateId" actually points to an email template
						if (template.templateType === Api.TemplateType.SocialMediaPost) {
							loadFromSocialMediaTemplate(template);
							return template;
						} else if (template.templateType === Api.TemplateType.Email && template.associatedTemplates?.length > 0) {
							try {
								const socialMediaTemplate = await templates.getById(template.associatedTemplates[0].id);
								loadFromSocialMediaTemplate(socialMediaTemplate);
								return socialMediaTemplate;
							} catch (err) {
								errorMessages.pushApiError(err);

								logApiError('LoadTemplate-Error', err);
							}
						}
					} catch (err) {
						errorMessages.pushApiError(err);

						logApiError('LoadTemplate-Error', err);
					}
				};

				if (postReport || postToEdit) {
					try {
						const post = (await postReport?.loadPost()) || (await postToEdit?.load());
						if (post) {
							if (post?.templateReference?.templateId) {
								try {
									const template = await templates.getById(post.templateReference.templateId);
									if (template.templateType === Api.TemplateType.SocialMediaPost) {
										context.setTemplate(template);
									} else if (
										template.templateType === Api.TemplateType.Email &&
										template.associatedTemplates?.length > 0
									) {
										const socialMediaTemplate = await templates.getById(template.associatedTemplates[0].id);

										context.setTemplate(socialMediaTemplate);
									}
								} catch (err) {
									// @ts-ignore
									if (err?.systemCode !== 404) {
										// skip error message when original social template has been deleted

										errorMessages.pushApiError(err);

										logApiError('LoadTemplate-Error', err);
									}
								}
							}
							// WIP: once post data is backfilled this can be changed to simply assign post.target

							const targetsMap: Api.IPostTarget[] =
								post?.designatedTargets?.length > 0 ? post.designatedTargets : post?.targets;

							context.setContent(getSocialMediaPlainTextContentFromRawRichContent(post?.content));

							context.setImages(post.attachments);

							context.setSelectedCreator(post.creator);

							context.setPostTargets(targetsMap);

							context.setHasChanges(false);

							context.setCampaignName(post.name);
							context.setOverlayLogo(post.overlayLogo);
							setScheduledPost(
								postToEdit || new Api.SocialMediaPostViewModel(userSession, post).impersonate(impersonationCtx)
							);
						}
					} catch (err) {
						errorMessages.pushApiError(err);

						logApiError('LoadPost-Error', err);
					}
				} else if (locationStateCreateRequest?.context) {
					if (typeof locationStateCreateRequest.context === 'string') {
						try {
							await resolveSocialMediaPostTemplate(locationStateCreateRequest.context);
						} catch (err) {
							errorMessages.pushApiError(err);

							logApiError('LoadTemplate-Error', err);
						}
					} else {
						loadFromSocialMediaTemplate(locationStateCreateRequest.context);
					}
				} else if (templateId) {
					try {
						await resolveSocialMediaPostTemplate(templateId);
					} catch (err) {
						errorMessages.pushApiError(err);

						logApiError('LoadTemplate-Error', err);
					}
				}

				if (locationStateCreateRequest?.sendFromUser && !source) {
					context.setSelectedCreator(locationStateCreateRequest.sendFromUser);
				} else if (!impersonationCtx && !context.selectedCreator && !source) {
					context.setSelectedCreator(userSession.user);
				}

				setIsLoaded(true);
				setIsLoading(false);
			})();
		}
	}, [
		context,
		errorMessages,
		h.location?.state?.viewModel,
		impersonationCtx,
		initialCampaignName,
		isLoaded,
		isLoading,
		isTestimonialMode,
		locationStateCreateRequest,
		locationStateCreateRequest?.context,
		locationStateCreateRequest?.sendFromUser,
		logApiError,
		postReport,
		postToEdit,
		scheduledPost,
		socialMediaConnections?.length,
		source,
		templateId,
		templates,
		testimonialSource,
		userSession,
	]);

	React.useEffect(() => {
		if (isLoaded && !userIsLoaded) {
			(async () => {
				if (!socialMediaConnections?.length && context.selectedCreator && isLoaded && !userIsLoaded) {
					const activeUser = new Api.UserViewModel(userSession, context.selectedCreator).impersonate(impersonationCtx);

					initialSelectedUserOptionRef.current = {
						dataContext: activeUser,
						id: activeUser.id,
					};
					try {
						const { socialMediaConnectedAccounts } = await activeUser.load();
						const activeConnections = socialMediaConnectedAccounts?.filter(
							x =>
								(x.state === Api.SocialMediaConnectionState.Connected ||
									x.state === Api.SocialMediaConnectionState.Disconnected) &&
								!!x.postTargetId
						);
						// Update postTargets based on active connections when relavent
						// The backend should be properly updating lastConnectionState to contain a valid postTargetId but the following
						// is a check to ensure display names etc display with "unknown" rather than null or undefined
						let selectedTargetConnections = context?.postTargets?.map(x =>
							x.state?.lastConnectionState?.postTargetId
								? x.state?.lastConnectionState
								: {
										postTargetDisplayName: 'Unknown',
										postTargetId: x?.pageId,
										state: Api.SocialMediaConnectionState.Unknown,
										type: x.provider,
										userId: x?.userId || x?.state?.lastConnectionState?.userId,
										userName: x?.state?.lastConnectionState?.userName || 'Unknown',
									}
						);

						// extra connections defines the set of connections that are any connections remaining in activeConnections that are not part
						// of the selected postTargets
						const extraConnections =
							activeConnections?.filter(
								x => !selectedTargetConnections?.find(y => y?.postTargetId === x.postTargetId && y?.type === x.type)
							) || [];
						if (!source) {
							setSocialMediaConnections(activeConnections);
						} else if (
							isScheduledOrPendingOrDraftOrNew(source) &&
							context.selectedCreator?.id === userSession.user?.id
						) {
							// When an existing post is editable social media connections are set as the union of selectedTargetConnections and extraConnections
							if (
								extraConnections?.length > 0 &&
								selectedTargetConnections?.filter(x => !x?.postTargetId)?.length > 0 &&
								context?.postTargets?.length > 0
							) {
								// If we are in a data state where one of the selectedPostTargets (and therefore selectedTargetConnections) does not have a valid postTargetId
								// we need to update the postTargets and the selectedTargetConnections
								// This is a failsafe for any bug in the backend where the postTargetId is not being set correctly
								// Replace postTargets with a userId match from extraConnections and update selectedTargetConnections to match
								const updatedTargets = context?.postTargets?.map(x => {
									if (x.state?.lastConnectionState?.postTargetId) {
										return x;
									} else {
										const matchTargetConnection = extraConnections?.find(
											y =>
												y?.type === x.provider && y?.userId === x?.state?.lastConnectionState?.userId && y?.postTargetId
										);
										const matchTargetConnectionIndex = extraConnections?.findIndex(
											y =>
												y?.type === x.provider && y?.userId === x?.state?.lastConnectionState?.userId && y?.postTargetId
										);
										if (matchTargetConnectionIndex > -1) {
											extraConnections?.splice(matchTargetConnectionIndex, 1);
										}
										return {
											pageId: matchTargetConnection?.postTargetId,
											provider: x.provider,
											state: {
												lastConnectionState: matchTargetConnection,
												status: Api.PostTargetStatus.Unknown,
											},
											userId: x.userId,
										};
									}
								});

								context.setPostTargets(updatedTargets);
								selectedTargetConnections = updatedTargets?.map(x =>
									x.state?.lastConnectionState?.postTargetId
										? x.state?.lastConnectionState
										: {
												postTargetDisplayName: 'Unknown',
												postTargetId: x?.pageId,
												state: Api.SocialMediaConnectionState.Unknown,
												type: x.provider,
												userId: x?.userId || x?.state?.lastConnectionState?.userId,
												userName: x?.state?.lastConnectionState?.userName || 'Unknown',
											}
								);
							}

							setSocialMediaConnections(selectedTargetConnections.concat(extraConnections));
						} else {
							setSocialMediaConnections(selectedTargetConnections);
						}

						if (!context.postTargets.length) {
							const selectedTargets: Api.IPostTarget[] = activeConnections
								?.filter(z => z.state === Api.SocialMediaConnectionState.Connected) // for setting targets only
								.map(x => ({
									pageId: x.postTargetId,
									provider: x.type,
									userId: x.userId,
								}))
								.filter(y =>
									locationStateCreateRequest?.targets?.length > 0
										? locationStateCreateRequest?.targets?.includes(y.provider)
										: y
								);

							context.setPostTargets(selectedTargets);
						}
					} catch (err) {
						errorMessages.pushApiError(err);

						logApiError('SetUserConnections-Error', err);
					}
				}
				setUserIsLoaded(true);
			})();
		}
	}, [
		socialMediaConnections?.length,
		context.selectedCreator,
		isLoaded,
		userIsLoaded,
		context,
		userSession,
		impersonationCtx,
		source,
		locationStateCreateRequest?.targets,
		errorMessages,
		logApiError,
	]);

	return {
		context,
		duplicateType,
		errorMessages,
		h,
		impersonationCtx,
		initialSelectedUserOptionRef,
		isLoaded,
		isLoading,
		isReviewMode: isTestimonialMode,
		isTestimonialMode,
		locationStateCreateRequest,
		logApiError,
		postReport,
		postToEdit,
		reviewData: testimonialSource,
		routeMatch,
		scheduledPost,
		setScheduledPost,
		setShowDuplicateWarning,
		setSocialMediaConnections,
		showDuplicateWarning,
		socialMediaConnections,
		source,
		suggestionId,
		templates,
		templateId,
		userIsLoaded,
		initialCampaignName,
	} as const;
}
